import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import * as S from './ProductInstallers.style';
import { Button, Flex, Grid, Header, useStyles } from '@archipro-design/aria';
import { getProfessionalTile } from '@modules/professionals-category/util/get-professional-tile';
import { Link, useNavigation } from '@remix-run/react';
import { useTileHotButtons } from '~/modules/root';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import { MEDIA_CONIDTION_SIZES_LARGE_SCREEN } from '~/modules/images/config/sizes-constant';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import { pxToRem } from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import type { ProfessionalTileViewModel } from '~/modules/professionals-category/type';
import { useState } from 'react';

interface ProductInstallersProps {
    data: ProductPageLoaderData['product'];
}

const INITIAL_SHOW_COUNT = 4;

const ProductInstallers = ({ data }: ProductInstallersProps) => {
    const { css } = useStyles();
    const navigation = useNavigation();
    const [showAll, setShowAll] = useState(false);
    const professionals = data.Installers?.Professionals ?? [];
    const { favouriteItem } = useSaveToDesignBoard('ProfessionalPin');
    const tracker = useTracker();

    const { getTileTopActions, getTileAdminCaption, appDisplayMode } =
        useTileHotButtons({
            itemType: 'professional',
            itemIDs: professionals.map((professional) => professional.ID),
        });
    const { mobile: isMobile } = appDisplayMode;

    const gridColumns = isMobile ? 1 : INITIAL_SHOW_COUNT;

    const imageSizes = isMobile
        ? '100vw'
        : `${MEDIA_CONIDTION_SIZES_LARGE_SCREEN} 33vw, 50vw`;

    const totalCount = professionals.length ?? 0;
    const isLoading = navigation.state === 'loading';

    const professionalTiles =
        professionals.map((professional, index) => {
            const data: ProfessionalTileViewModel = {
                ID: professional.ID,
                type: 'professional',
                image: professional.Thumbnail?.desktop ?? '',
                logo: professional.CompanyLogo ?? '',
                logoBackgroundColor:
                    professional.LogoBackgroundColor ?? '#ffffff',
                title: professional.Title,
                memberSince: professional.ProfileLiveDate?.split('-')[0],
                link: professional.Link,
                category: professional.PrimaryCategory?.PrettyTitle ?? '',
            };

            const final = getProfessionalTile(data, (link) => ({
                as: Link,
                to: link,
            }));

            const topActions = getTileTopActions({
                item: professional,
            });
            const adminCaption = getTileAdminCaption(professional.ID);

            const cutOff = !showAll && index + 1 > INITIAL_SHOW_COUNT;

            return (
                <Link
                    key={`featured-professional-tile-${professional.ID}`}
                    to={professional.Link}
                    prefetch={'intent'}
                    className={css(cutOff ? S.TileCutOff : S.Tile)}
                >
                    <ProfessionalTile
                        title={final.title}
                        image={[
                            {
                                src: data.image ?? '',
                                width: '100%',
                                height: '100%',
                                size: imageSizes,
                                alt: final.title ?? '',
                                sizes: getImagesSizes({
                                    desktop: 595,
                                    mobile: 378,
                                }),
                            },
                        ]}
                        logo={{
                            image: {
                                src: final.logo.image ?? '',
                                alt: `${final.title} professional logo`,
                                loading: 'lazy',
                            },
                            variables: final.logo.variables,
                        }}
                        memberSince={final.memberSince}
                        variant={'04'}
                        size={isMobile ? 'small' : 'large'}
                        hideFavourite={cutOff}
                        onFavourite={(e) =>
                            favouriteItem.onClick(e, professional.ID)
                        }
                        variables={{ tilePadding: pxToRem(10) }}
                        isFavourited={favouriteItem.favourited(professional.ID)}
                        onClick={() => {
                            tracker.log('ProductProfessionalInstallerClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }}
                        impresion={{
                            professionalID: professional.ID,
                            itemID: professional.ID,
                            type: 'LibraryItem_Directory',
                        }}
                        topActions={topActions}
                        adminCaption={adminCaption}
                        category={final.category}
                    />
                </Link>
            );
        }) ?? null;

    if (!data.HasInstallers) {
        return null;
    }

    return (
        <div className={css(S.ProfessionalsWrapper)}>
            <Header
                variant="03"
                className={css(S.Header)}
                as="h3"
                variables={{
                    fontSize: pxToRem(18),
                }}
            >
                Installers of
                <br />
                {data.Title}
            </Header>
            <Grid columns={gridColumns}>{professionalTiles}</Grid>
            {!showAll && totalCount > INITIAL_SHOW_COUNT && (
                <Flex hAlign="start">
                    <Button
                        loading={isLoading}
                        outlined={true}
                        className={css(S.LoadMoreButton)}
                        size={16}
                        style={{
                            textTransform: isLoading ? 'unset' : 'uppercase',
                        }}
                        onClick={() => {
                            tracker.log('ProductProfessionalInstallerClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                            setShowAll(true);
                        }}
                    >
                        {!isLoading ? `View more` : 'Loading...'}
                    </Button>
                </Flex>
            )}
        </div>
    );
};

export default ProductInstallers;
